<template>
  <div class="container mx-auto">
    <!-- Barra de búsqueda y filtros -->
    <div class="flex justify-between mb-4" v-if="mode === 'cart'">
      <text-input 
        placeholder="Buscar producto por nombre o código..." 
        id="search" 
        class="p-2 border border-gray-300 rounded w-2/3"
        v-model="form.search.terms"
        autofocus
      />
      <select 
        v-model="form.search.category" 
        class="p-2 border border-gray-300 rounded w-1/3 ml-2"
      >
        <option value="">Todas las categorías</option>
        <option 
          v-for="category in product_categories" 
          :key="category.id" 
          :value="category.id"
        >
          {{ category.name }}
        </option>
      </select>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 gap-4">
      <!-- Lista de productos -->
      <div class="col-span-2">
        <div v-if="mode === 'cart'" class="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 max-h-[600px] overflow-y-auto">
          <product-card 
            v-for="product in products.data" 
            :key="product.id" 
            :product="product" 
            @addCart="addCart" 
            :change="change" 
            :cop_change="cop_change"
          />
        </div>
      </div>

      <!-- Carrito de compras -->
      <div v-if="mode === 'cart'" class="bg-white rounded-lg shadow-lg p-6">
        <h2 class="text-xl font-bold mb-6">Carrito de Compras</h2>

        <div class="space-y-4 max-h-[400px] overflow-y-auto">
          <div 
            v-for="(product, key) in cart" 
            :key="product.id" 
            class="flex flex-col p-4 border rounded-lg bg-white shadow-sm hover:shadow transition-shadow"
          >
            <!-- Header: Name, Stock, Delete -->
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1 pr-2">
                <h3 class="font-bold text-gray-800 leading-tight">{{ product?.name }}</h3>
                <p class="text-xs text-gray-500 mt-1">Stock: {{ product?.stock }} unidades</p>
              </div>
              <button 
                @click="removeProduct(product)" 
                class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                title="Eliminar producto"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>

            <!-- Controls: Quantity & Price Type / Plan Selector / Modalidad Bs -->
            <div class="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label class="block text-xs text-gray-500 mb-1">Cantidad</label>
                <text-input-rounded 
                  type="number" 
                  :max="product?.stock"
                  min="1"
                  v-model="cart[key]['quantity']" 
                  class="w-full text-center" 
                />
              </div>

              <!-- Selector específico si es producto de Planes -->
              <div v-if="isPlanProduct(product.code)">
                <div class="flex items-center justify-between mb-1">
                  <label class="block text-xs text-gray-500">Plan</label>
                  <button 
                    type="button" 
                    @click="openPlanConfigModal(product, key)"
                    class="text-[11px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors cursor-pointer hover:underline"
                    title="Administrar Planes"
                  >
                    <i class="fa fa-cog text-[10px]"></i>
                    <span>Administrar</span>
                  </button>
                </div>
                <select 
                  v-model="cart[key]['selected_plan']"
                  @change="onPlanChange(key)"
                  class="w-full rounded-md border-gray-300 text-xs py-1.5 px-2 font-medium focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option 
                    v-for="plan in getProductPlans(product)" 
                    :key="plan.name" 
                    :value="plan.name"
                  >
                    {{ plan.label }}
                  </option>
                  <option value="__config__" class="font-medium text-blue-600 bg-blue-50">
                    ⚙️ Administrar planes...
                  </option>
                </select>
              </div>

              <!-- Indicador si es producto de Monto en Bs -->
              <div v-else-if="isCustomBsProduct(product.code)">
                <label class="block text-xs font-semibold text-emerald-700 mb-1">Modalidad</label>
                <div class="py-1.5 px-2 bg-emerald-50 text-emerald-800 rounded-md text-xs font-bold border border-emerald-200 text-center">
                  Monto en Bs.
                </div>
              </div>

              <!-- Selector estándar para los demás productos -->
              <div v-else>
                <label class="block text-xs text-gray-500 mb-1">Tipo de Precio</label>
                <select 
                  v-model="cart[key]['priceType']"
                  @change="onPriceTypeChange(key)"
                  class="w-full rounded-md border-gray-300 text-sm py-1.5 px-2"
                >
                  <option 
                    v-for="group in groups" 
                    :key="group.id" 
                    :value="'group-'+group.id"
                  >
                    {{ group.name }} - ${{ getGroupPrice(product.prices, group.id) }}
                  </option>
                  <option v-if="$page.props.auth.user.role !== 'sales'" value="custom">Personalizado</option>
                </select>
              </div>
            </div>

            <!-- Price Input and Total -->
            <!-- Caso 1: Producto personalizado en Bs -->
            <div v-if="isCustomBsProduct(product.code)" class="border-t pt-3 mt-1 space-y-2 bg-emerald-50/50 p-2.5 rounded-lg border border-emerald-100">
              <div class="flex items-center justify-between">
                <div class="w-1/2 pr-2">
                  <label class="block text-xs font-semibold text-emerald-800 mb-1">Monto en Bs:</label>
                  <div class="flex items-center gap-1.5">
                    <span class="text-emerald-700 font-bold text-sm">Bs.</span>
                    <input
                      v-model="cart[key]['price_bs']"
                      @input="onPriceBsChange(key)"
                      type="number"
                      step="any"
                      min="0"
                      placeholder="0.00"
                      class="w-full text-right rounded-md border-emerald-300 py-1.5 px-2 text-sm font-bold text-emerald-900 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    />
                  </div>
                </div>
                <div class="w-1/2 pl-2 text-right">
                  <div class="text-xs text-gray-500">Equiv. USD</div>
                  <div class="text-sm font-bold text-gray-800">
                    ${{ Number(cart[key]['price'] || 0).toFixed(2) }}
                  </div>
                  <div class="text-[11px] text-gray-400">Tasa: Bs. {{ change }}</div>
                </div>
              </div>
              <div class="flex items-center justify-between border-t border-emerald-200/60 pt-1.5">
                <div class="text-xs text-gray-600">Total Producto:</div>
                <div class="text-base font-bold text-emerald-800">
                  Bs. {{ (Number(cart[key]['price_bs'] || 0) * product.quantity).toFixed(2) }}
                  <span class="text-xs font-normal text-gray-500">(${{ (cart[key]['price'] * product.quantity).toFixed(2) }})</span>
                </div>
              </div>
            </div>

            <!-- Caso 2: Producto de Planes -->
            <div v-else-if="isPlanProduct(product.code)" class="border-t pt-3 mt-1 space-y-2 bg-blue-50/50 p-2.5 rounded-lg border border-blue-100">
              <div class="flex items-center justify-between">
                <div class="w-1/2 pr-2">
                  <label class="block text-xs font-semibold text-blue-800 mb-1">Precio Plan (Bs.):</label>
                  <div class="flex items-center gap-1.5">
                    <span class="text-blue-700 font-bold text-sm">Bs.</span>
                    <input
                      v-model="cart[key]['price_bs']"
                      @input="onPriceBsChange(key)"
                      type="number"
                      step="any"
                      min="0"
                      placeholder="0.00"
                      class="w-full text-right rounded-md border-blue-300 py-1.5 px-2 text-sm font-bold text-blue-900 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                  </div>
                </div>
                <div class="w-1/2 pl-2 text-right">
                  <div class="text-xs text-gray-500">Equiv. USD</div>
                  <div class="text-sm font-bold text-gray-800">
                    ${{ Number(cart[key]['price'] || 0).toFixed(2) }}
                  </div>
                  <div class="text-[11px] text-gray-400">Tasa: Bs. {{ change }}</div>
                </div>
              </div>
              <div class="flex items-center justify-between border-t border-blue-200/60 pt-1.5">
                <div class="text-xs text-gray-600">Total Plan:</div>
                <div class="text-base font-bold text-blue-800">
                  Bs. {{ (Number(cart[key]['price_bs'] || 0) * product.quantity).toFixed(2) }}
                  <span class="text-xs font-normal text-gray-500">(${{ (cart[key]['price'] * product.quantity).toFixed(2) }})</span>
                </div>
              </div>
            </div>

            <!-- Caso 3: Producto estándar -->
            <div v-else class="flex items-center justify-between border-t pt-3 mt-1">
              <div class="flex items-center gap-1.5 w-1/2">
                <span class="text-gray-500 font-medium">$</span>
                <input
                  v-model="cart[key]['price']"
                  type="number"
                  :disabled="cart[key]['priceType'] !== 'custom' || (!product.quick && $page.props.auth.user.role === 'sales')"
                  class="w-full text-right rounded-md border-gray-300 py-1.5 px-2 text-sm font-medium focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                />
              </div>
              <div class="text-right w-1/2 pl-2">
                <div class="text-xs text-gray-500">Total</div>
                <div class="text-base font-bold text-gray-900">
                  ${{ (cart[key]['price'] * product.quantity).toFixed(2) }}
                </div>
              </div>
            </div>

            <!-- Nota obligatoria para recargas o cuando require_note es true -->
            <div v-if="product.require_note || isCustomBsProduct(product.code) || isPlanProduct(product.code)" class="mt-3">
              <label class="block text-xs font-medium text-gray-600 mb-1">
                {{ isCustomBsProduct(product.code) ? 'Número telefónico a recargar *' : (isPlanProduct(product.code) ? 'Plan y número / Nota *' : 'Nota requerida *') }}
              </label>
              <input 
                type="text" 
                v-model="cart[key]['note']" 
                class="w-full p-2 border border-gray-300 rounded-md text-sm bg-gray-50 focus:bg-white focus:ring-blue-500 focus:border-blue-500" 
                :placeholder="isCustomBsProduct(product.code) ? 'Ej: 04141234567' : (isPlanProduct(product.code) ? 'Ej: Plan 6GB - 04141234567' : 'Nota requerida *')"
                required
              />
            </div>
          </div>

          <div v-if="!cart.length" class="text-center text-gray-500 py-8">
            El carrito está vacío
          </div>
        </div>

        <!-- Totales -->
        <div class="mt-6 pt-4 border-t space-y-2">
          <div class="flex justify-between font-medium">
            <span>Total USD:</span>
            <span>${{ getTotal() }}</span>
          </div>
          <div class="flex justify-between text-gray-600">
            <span>Total Bs:</span>
            <span>{{ (getTotal() * change).toFixed(2) }}</span>
          </div>
          <div class="flex justify-between text-gray-600">
            <span>Total COP:</span>
            <span>{{ ((getTotal() * change) * cop_change).toFixed(1) }}</span>
          </div>
        </div>

        <div class="mt-4 flex flex-col gap-4">
          <div class="flex items-center">
            <input 
              type="checkbox" 
              v-model="withoutClient" 
              id="withoutClient"
              class="rounded border-gray-300"
            >
            <label for="withoutClient" class="ml-2 text-gray-700">
              Venta sin cliente registrado
            </label>
          </div>

          <div>
            <label class="block text-gray-700 mb-1 text-sm font-medium">Observación (Opcional)</label>
            <textarea 
              v-model="form.comentary" 
              class="w-full p-2 border border-gray-300 rounded-md text-sm" 
              rows="2" 
              placeholder="Ingrese observaciones importantes..."
            ></textarea>
          </div>
        </div>
      </div>

      <!-- Formulario de cliente -->
      <div v-if="mode === 'client'" class="col-span-3 bg-white rounded-lg shadow-lg p-6">
        <div class="flex items-center justify-between mb-4 border-b pb-4">
          <h2 class="text-xl font-bold text-gray-800">Datos del Cliente</h2>
          <button 
            type="button"
            @click="clearAllClientFields"
            class="px-4 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg shadow-sm flex items-center gap-2 transition-colors cursor-pointer"
            title="Limpiar todas las celdas del formulario"
          >
            <i class="fa fa-trash-alt text-gray-500"></i>
            <span>Limpiar todas las celdas</span>
          </button>
        </div>

        <!-- Aviso si el cliente tiene saldo pendiente / deuda registrada -->
        <div v-if="form.client.balance && parseFloat(form.client.balance) !== 0" class="mb-4 p-3 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg flex items-center justify-between shadow-sm">
          <div class="flex items-center gap-2 text-amber-900 text-sm">
            <i class="fa fa-exclamation-triangle text-amber-600 text-base"></i>
            <span>
              Saldo registrado del cliente: 
              <strong class="font-bold">
                {{ parseFloat(form.client.balance) > 0 ? `Deuda pendiente de $${parseFloat(form.client.balance).toFixed(2)}` : `Saldo a favor de $${Math.abs(parseFloat(form.client.balance)).toFixed(2)}` }}
              </strong>
            </span>
          </div>
          <button 
            type="button" 
            @click="resetBalance" 
            class="text-amber-800 hover:text-amber-950 text-xs font-semibold px-2.5 py-1 rounded bg-amber-200 hover:bg-amber-300 transition-colors flex items-center gap-1 cursor-pointer"
            title="Descartar aviso de saldo"
          >
            <i class="fa fa-times"></i>
            <span>Descartar</span>
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
          <!-- Identificación o Alias -->
          <div class="flex items-end gap-2">
            <text-input 
              v-model="form.client.identification" 
              label="Identificación o Alias"
              class="flex-1"
            />
            <button 
              class="btn-blue h-10 px-4 flex items-center justify-center gap-1.5"
              @click="findClient" 
              type="button"
              title="Buscar cliente"
            >
              <i class="fa fa-search" />
            </button>
            <button 
              type="button"
              @click="form.client.identification = ''"
              class="h-10 px-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-red-600 rounded flex items-center justify-center transition-colors cursor-pointer"
              title="Limpiar Identificación"
            >
              <i class="fa fa-trash-alt text-sm" />
            </button>
          </div>

          <!-- Nombre Completo -->
          <div class="flex items-end gap-2">
            <text-input 
              v-model="form.client.name" 
              label="Nombre Completo" 
              class="flex-1"
            />
            <button 
              type="button"
              @click="form.client.name = ''"
              class="h-10 px-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-red-600 rounded flex items-center justify-center transition-colors cursor-pointer"
              title="Limpiar Nombre Completo"
            >
              <i class="fa fa-trash-alt text-sm" />
            </button>
          </div>

          <!-- Teléfono -->
          <div class="flex items-end gap-2">
            <text-input 
              v-model="form.client.phone" 
              label="Teléfono" 
              class="flex-1"
            />
            <button 
              type="button"
              @click="form.client.phone = ''"
              class="h-10 px-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-red-600 rounded flex items-center justify-center transition-colors cursor-pointer"
              title="Limpiar Teléfono"
            >
              <i class="fa fa-trash-alt text-sm" />
            </button>
          </div>

          <!-- Dirección -->
          <div class="flex items-end gap-2">
            <text-input 
              v-model="form.client.address" 
              label="Dirección" 
              class="flex-1"
            />
            <button 
              type="button"
              @click="form.client.address = ''"
              class="h-10 px-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-red-600 rounded flex items-center justify-center transition-colors cursor-pointer"
              title="Limpiar Dirección"
            >
              <i class="fa fa-trash-alt text-sm" />
            </button>
          </div>
        </div>
      </div>

      <!-- Datos de pago -->
      <div v-if="mode === 'pay'" class="bg-white rounded-lg shadow-lg p-6">
        <h2 class="text-xl font-bold mb-6">Método de Pago</h2>

        <div class="space-y-4">
          <div>
            <label class="block text-gray-700 mb-2">Seleccione método de pago</label>
            <select 
              v-model="form.method"
              class="form-select w-full rounded-lg border-gray-300"
            >
              <option value="">Seleccionar método</option>
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia Bs</option>
              <option value="transferencia-usd">Transferencia USD</option>
              <option value="biopago">Zelle</option>
              <option value="credito">Crédito</option>
            </select>
          </div>

          <text-input 
            v-if="form.method && form.method !== 'efectivo'"
            v-model="form.referencePay" 
            label="Número de Referencia"
          />

          <text-input 
            v-if="form.method === 'efectivo'"
            v-model="cambio"
            type="number"
            label="Monto recibido" 
          />
        </div>
      </div>

      <!-- Comprobantes -->
      <div v-if="mode === 'print'" class="bg-white rounded-lg shadow-lg p-6">
        <h2 class="text-xl font-bold mb-6">Comprobantes de Pago</h2>
        
        <div class="flex gap-4">
          <button class="btn-blue" type="button">
            <i class="fa fa-print mr-2"></i> Nota de entrega
          </button>
          <button class="btn-blue" type="button">
            <i class="fa fa-print mr-2"></i> Factura
          </button>
          <button class="btn-blue" type="button">
            <i class="fa fa-print mr-2"></i> Ticket
          </button>
        </div>
      </div>
    </div>

    <!-- Barra inferior fija -->
    <div class="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4 flex items-center justify-between">
      <div class="space-x-4">
        <span v-if="cambio" class="font-medium">
          Cambio: ${{ (cambio - getTotal()).toFixed(2) }}
        </span>
        <span class="font-medium">
          Total: ${{ getTotal() }} | Bs.{{ (getTotal() * change).toFixed(2) }} | 
          COP {{ ((getTotal() * change) * cop_change).toFixed(1) }}
        </span>
      </div>

      <div class="space-x-2">
        <button 
          v-if="mode !== 'cart'"
          @click="back"
          class="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg"
        >
          Atrás
        </button>
        <button 
          @click="next"
          class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
        >
          Siguiente
        </button>
      </div>
    </div>

    <!-- Modal para configurar planes del producto -->
    <div v-if="showPlanModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100">
        <!-- Header -->
        <div class="bg-blue-600 text-white px-5 py-4 flex items-center justify-between">
          <div class="flex items-center gap-2.5">
            <i class="fa fa-cogs text-lg"></i>
            <div>
              <h3 class="font-bold text-base leading-tight text-white">Administrar Planes</h3>
              <p class="text-xs text-blue-100 font-normal mt-0.5">{{ editingPlanProduct?.name }}</p>
            </div>
          </div>
          <button 
            type="button" 
            @click="closePlanModal" 
            class="text-white hover:text-white/80 p-1.5 rounded-lg hover:bg-black/10 transition-colors flex items-center justify-center cursor-pointer"
            title="Cerrar"
          >
            <i class="fa fa-times text-lg"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <p class="text-xs text-blue-900 bg-blue-50 p-3 rounded-lg border border-blue-200 leading-relaxed">
            Aquí puedes <strong>agregar nuevos planes</strong>, <strong>modificar tarifas en Bolívares (Bs.)</strong> o <strong>eliminar planes</strong>. Los cambios se guardarán de inmediato para este producto.
          </p>

          <div class="space-y-3">
            <div 
              v-for="(plan, idx) in editingPlans" 
              :key="idx" 
              class="bg-white p-3 rounded-lg border border-gray-200 shadow-sm space-y-2 hover:border-blue-300 transition-colors"
            >
              <div class="flex items-center gap-2">
                <div class="flex-1">
                  <label class="block text-[11px] font-semibold text-gray-600 mb-0.5">Nombre del Plan</label>
                  <input 
                    type="text" 
                    v-model="plan.name" 
                    placeholder="Ej: Plan 6GB" 
                    class="w-full text-xs font-semibold border-gray-300 rounded px-2.5 py-1.5 focus:ring-blue-500 focus:border-blue-500 bg-gray-50/50"
                  />
                </div>
                <div class="w-36">
                  <label class="block text-[11px] font-semibold text-gray-600 mb-0.5">Tarifa en Bs.</label>
                  <div class="flex items-center gap-1">
                    <span class="text-xs font-bold text-blue-700">Bs.</span>
                    <input 
                      type="number" 
                      step="any" 
                      min="0" 
                      v-model.number="plan.price" 
                      placeholder="0.00" 
                      class="w-full text-xs font-bold text-right border-gray-300 rounded px-2 py-1.5 focus:ring-blue-500 focus:border-blue-500 text-blue-900 bg-gray-50/50"
                    />
                  </div>
                </div>
                <div class="pt-4 flex justify-center">
                  <button 
                    type="button" 
                    @click="removePlanRow(idx)" 
                    class="text-red-400 hover:text-red-600 p-1.5 rounded hover:bg-red-50 transition-colors cursor-pointer"
                    title="Eliminar este plan"
                  >
                    <i class="fa fa-trash-alt text-xs"></i>
                  </button>
                </div>
              </div>
              <div class="flex items-center justify-between text-[11px] text-gray-500 pt-1 border-t border-gray-100">
                <span>Equivalente referencial:</span>
                <span class="font-semibold text-gray-700">
                  ${{ (change > 0 && plan.price > 0) ? (Number(plan.price) / Number(change)).toFixed(2) : '0.00' }} USD
                </span>
              </div>
            </div>
          </div>

          <button 
            type="button" 
            @click="addPlanRow" 
            class="w-full py-2.5 border-2 border-dashed border-blue-300 hover:border-blue-500 bg-blue-50/30 hover:bg-blue-50 text-blue-700 text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
          >
            <i class="fa fa-plus-circle text-sm text-blue-600"></i>
            <span>+ Añadir Nuevo Plan</span>
          </button>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-5 py-3 border-t flex justify-end gap-2">
          <button 
            type="button" 
            @click="closePlanModal" 
            class="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button 
            type="button" 
            @click="savePlansConfig" 
            :disabled="savingPlans" 
            class="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <i v-if="savingPlans" class="fa fa-spinner fa-spin"></i>
            <span>{{ savingPlans ? 'Guardando...' : 'Guardar Cambios' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import LayoutFull from '../../Shared/LayoutFull.vue'
import ProductCard from '../../Shared/ProductCard.vue'
import TextInput from '../../Shared/TextInput.vue'
import TextInputRounded from '../../Shared/TextInputRounded.vue'
import pickBy from 'lodash/pickBy'
import SelectInput from '../../Shared/SelectInput.vue'

export default {
  components: { 
    ProductCard, 
    TextInput, 
    TextInputRounded, 
    SelectInput 
  },

  layout: LayoutFull,

  props: {
    products: Object,
    change: Number,
    cop_change: Number,
    client: Object,
    product_categories: Object,
    groups: Array,
    default_generic_client: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      cambio: 0,
      showClientCreate: false,
      mode: 'cart',
      form: this.$inertia.form({
        search: {
          terms: '',
          category: ''
        },
        client: {
          identification: '',
          name: '',
          email: '',
          phone: '',
          address: '',
          balance: 0,
          typeClient: 'a'
        },
        products: null,
        isCredit: false,
        comentary: '',
        referencePay: '',
        quick: true
      }),
      cart: [],
      withoutClient: this.default_generic_client,
      showPlanModal: false,
      editingPlanProduct: null,
      editingPlanCartKey: null,
      editingPlans: [],
      savingPlans: false
    }
  },

  watch: {
    'form.search.terms': function() {
      this.onSearchProductsChange()
    },

    'form.search.category': function() {
      this.onSearchProductsChange()
    },

    'products': function(val) {
      if(this.products.data.length === 1) {
        const product = this.products.data[0]
        
        if(product.code === this.form.search.terms) {
          if (product.quantity <= 0) {
            this.form.search.terms = ''
            return alert('No hay stock disponible de este producto')
          }

          const existingProduct = this.cart.find(p => p.id === product.id)

          if (existingProduct) {
            existingProduct.quantity++
          } else {
            this.cart.push(this.buildCartItem(product))
          }

          this.form.search.terms = ''
        }
      }
    }
  },

  methods: {
    cleanCode(code) {
      return (code || '').replace(/[\s\-_.]/g, '').toUpperCase()
    },

    isPlanProduct(code) {
      const c = this.cleanCode(code)
      return c === 'RECARGAPLANES' || c === 'RECARGA2'
    },

    isCustomBsProduct(code) {
      const c = this.cleanCode(code)
      return c === 'RECARGAVTA' || c === 'RECARGAREEMPLAZO'
    },

    getProductPlans(product) {
      const code = typeof product === 'string' ? product : product?.code
      const c = this.cleanCode(code)

      // 1. Revisar si el producto tiene planes personalizados guardados en description (JSON)
      if (typeof product === 'object' && product?.description) {
        try {
          const parsed = typeof product.description === 'string' ? JSON.parse(product.description) : product.description
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name) {
            return [
              ...parsed.map((p) => ({
                name: p.name,
                price: Number(p.price || 0),
                label: `${p.name} - ${Number(p.price || 0).toLocaleString('es-VE')} Bs.`
              })),
              { name: 'Personalizado', price: 0, label: 'Otro monto (Personalizado en Bs.)' }
            ]
          }
        } catch (e) {}
      }

      // 2. Precios por grupo configurados
      const prices = (typeof product === 'object' && product?.prices) ? product.prices : []
      const p1 = (prices && prices[0] && parseFloat(prices[0].price) > 0) ? parseFloat(prices[0].price) : null
      const p2 = (prices && prices[1] && parseFloat(prices[1].price) > 0) ? parseFloat(prices[1].price) : null
      const p3 = (prices && prices[2] && parseFloat(prices[2].price) > 0) ? parseFloat(prices[2].price) : null

      if (c === 'RECARGAPLANES') {
        const price1 = p1 !== null ? p1 : 5500
        const price2 = p2 !== null ? p2 : 10000
        const price3 = p3 !== null ? p3 : 20000
        return [
          { name: 'Plan 6GB', price: price1, label: `Plan 6GB - ${Number(price1).toLocaleString('es-VE')} Bs.` },
          { name: 'Plan 12GB', price: price2, label: `Plan 12GB - ${Number(price2).toLocaleString('es-VE')} Bs.` },
          { name: 'Plan 30GB', price: price3, label: `Plan 30GB - ${Number(price3).toLocaleString('es-VE')} Bs.` },
          { name: 'Personalizado', price: 0, label: 'Otro monto (Personalizado en Bs.)' },
        ]
      }
      if (c === 'RECARGA2') {
        const price1 = p1 !== null ? p1 : 300
        const price2 = p2 !== null ? p2 : 500
        const price3 = p3 !== null ? p3 : 1000
        return [
          { name: 'Plan 6GB', price: price1, label: `Plan 6GB - ${Number(price1).toLocaleString('es-VE')} Bs.` },
          { name: 'Plan 12GB', price: price2, label: `Plan 12GB - ${Number(price2).toLocaleString('es-VE')} Bs.` },
          { name: 'Plan 30GB', price: price3, label: `Plan 30GB - ${Number(price3).toLocaleString('es-VE')} Bs.` },
          { name: 'Personalizado', price: 0, label: 'Otro monto (Personalizado en Bs.)' },
        ]
      }
      return []
    },

    openPlanConfigModal(product, key) {
      this.editingPlanProduct = product
      this.editingPlanCartKey = key
      const currentPlans = this.getProductPlans(product).filter(p => p.name !== 'Personalizado')
      this.editingPlans = currentPlans.map(p => ({
        name: p.name,
        price: p.price
      }))
      if (this.editingPlans.length === 0) {
        this.editingPlans = [
          { name: 'Plan 6GB', price: 5500 },
          { name: 'Plan 12GB', price: 10000 },
          { name: 'Plan 30GB', price: 20000 },
        ]
      }
      this.showPlanModal = true
    },

    closePlanModal() {
      this.showPlanModal = false
      this.editingPlanProduct = null
      this.editingPlanCartKey = null
      this.editingPlans = []
      this.savingPlans = false
    },

    addPlanRow() {
      this.editingPlans.push({
        name: `Plan ${this.editingPlans.length + 1}`,
        price: 0
      })
    },

    removePlanRow(idx) {
      if (this.editingPlans.length <= 1) {
        return alert('Debe haber al menos un plan configurado.')
      }
      this.editingPlans.splice(idx, 1)
    },

    async savePlansConfig() {
      if (!this.editingPlanProduct || !this.editingPlanProduct.id) return
      
      const invalid = this.editingPlans.find(p => !p.name || p.name.trim() === '' || isNaN(p.price) || p.price < 0)
      if (invalid) {
        return alert('Todos los planes deben tener un nombre y un precio en Bs válido (mayor o igual a 0).')
      }

      this.savingPlans = true
      try {
        const response = await axios.post(`/products/${this.editingPlanProduct.id}/plans`, {
          plans: this.editingPlans
        })

        const updatedDesc = response.data.description || JSON.stringify(this.editingPlans)
        
        // 1. Actualizar descripción en el producto del carrito
        if (this.editingPlanCartKey !== null && this.cart[this.editingPlanCartKey]) {
          this.cart[this.editingPlanCartKey].description = updatedDesc
          const newPlans = this.getProductPlans(this.cart[this.editingPlanCartKey])
          const exists = newPlans.find(p => p.name === this.cart[this.editingPlanCartKey].selected_plan)
          if (!exists && newPlans.length > 0) {
            this.cart[this.editingPlanCartKey].selected_plan = newPlans[0].name
          }
          this.onPlanChange(this.editingPlanCartKey)
        }

        // 2. Actualizar también en products.data
        if (this.products?.data) {
          const prodInData = this.products.data.find(p => p.id === this.editingPlanProduct.id)
          if (prodInData) {
            prodInData.description = updatedDesc
          }
        }

        this.closePlanModal()
        alert('Planes actualizados con éxito.')
      } catch (error) {
        console.error('Error al guardar planes:', error)
        alert('Ocurrió un error al guardar los planes. Por favor intente de nuevo.')
      } finally {
        this.savingPlans = false
      }
    },

    buildCartItem(product) {
      const isPlan = this.isPlanProduct(product.code)
      const isBs = this.isCustomBsProduct(product.code)

      let defaultPrice = product?.prices?.length ? product.prices[0].price : 0
      let defaultGroupId = product?.prices?.length ? product.prices[0].group_id : null
      let priceType = defaultGroupId ? `group-${defaultGroupId}` : 'custom'
      let note = product.note || ''
      let selectedPlan = null
      let selectedPlanPrice = null
      let priceBs = null

      if (isPlan) {
        const plans = this.getProductPlans(product)
        if (plans.length > 0) {
          const firstPlan = plans[0]
          selectedPlan = firstPlan.name
          selectedPlanPrice = firstPlan.price
          priceBs = firstPlan.price
          const rate = this.change && this.change > 0 ? this.change : 1
          defaultPrice = Number((firstPlan.price / rate).toFixed(4))
          note = firstPlan.name
          priceType = 'plan'
        }
      } else if (isBs) {
        priceType = 'custom'
        defaultPrice = 0
        priceBs = ''
      }

      return {
        ...product,
        stock: product.quantity,
        quantity: 1,
        price: defaultPrice,
        price_bs: priceBs,
        selected_plan: selectedPlan,
        selectedPlanPrice: selectedPlanPrice,
        priceType: priceType,
        note: note,
        require_note: isPlan || isBs ? true : !!product.require_note,
        quick: false
      }
    },

    onPlanChange(key) {
      const item = this.cart[key]
      if (!item) return

      if (item.selected_plan === '__config__') {
        const plans = this.getProductPlans(item).filter(p => p.name !== 'Personalizado')
        item.selected_plan = plans.length > 0 ? plans[0].name : null
        this.openPlanConfigModal(item, key)
        return
      }

      const plans = this.getProductPlans(item)
      const selected = plans.find(p => p.name === item.selected_plan)
      if (selected) {
        if (selected.name === 'Personalizado') {
          item.price_bs = ''
          item.price = 0
          item.note = 'Recarga Plan'
        } else {
          item.price_bs = selected.price
          item.selectedPlanPrice = selected.price
          const rate = this.change && this.change > 0 ? this.change : 1
          item.price = Number((selected.price / rate).toFixed(4))
          const planNames = plans.map(p => p.name)
          if (!item.note) {
            item.note = selected.name
          } else {
            const matchedName = planNames.find(n => item.note.startsWith(n))
            if (matchedName) {
              item.note = selected.name + item.note.substring(matchedName.length)
            } else {
              item.note = `${selected.name} - ${item.note}`
            }
          }
        }
      }
    },

    onPriceBsChange(key) {
      const item = this.cart[key]
      if (!item) return
      const val = parseFloat(item.price_bs)
      if (!isNaN(val) && val > 0) {
        const rate = this.change && this.change > 0 ? this.change : 1
        item.price = Number((val / rate).toFixed(4))
      } else {
        item.price = 0
      }
    },

    getGroupPrice(prices, groupId) {
      const match = prices?.find(p => p.group_id === groupId)
      return match ? match.price : '-'
    },
    
    onPriceTypeChange(index) {
      const product = this.cart[index]
      if (!product) return

      if (this.isPlanProduct(product.code) || this.isCustomBsProduct(product.code)) {
        return
      }

      if (product.priceType === 'custom') {
        if (this.$page.props.auth.user.role === 'sales') {
          const fallbackGroupId = product?.prices?.length ? product.prices[0].group_id : null
          product.priceType = fallbackGroupId ? `group-${fallbackGroupId}` : null
        }
        return
      }

      if (product.priceType && product.priceType.startsWith('group-')) {
        const groupId = parseInt(product.priceType.replace('group-', ''))
        const price = this.getGroupPrice(product.prices, groupId)
        if (price !== '-') {
          this.cart[index].price = price
        }
      }
    },
    getTotal() {
      return this.cart.reduce((total, product) => {
        return total + (product.price * product.quantity)
      }, 0).toFixed(2)
    },

    back() {
      if(this.mode === 'client') {
        this.mode = 'cart'
      }
    },

    next() {
      if(this.mode === 'cart') {
        if(this.cart.length === 0) {
          return alert("Debe agregar productos al carrito para continuar")
        }

        const missingBsPrice = this.cart.find(p => (this.isCustomBsProduct(p.code) || this.isPlanProduct(p.code)) && (!p.price_bs || Number(p.price_bs) <= 0));
        if (missingBsPrice) {
          return alert(`Debe ingresar un monto en Bs válido para "${missingBsPrice.name}".`);
        }

        const missingNote = this.cart.find(p => (p.require_note || this.isCustomBsProduct(p.code) || this.isPlanProduct(p.code)) && (!p.note || p.note.trim() === ''));
        if (missingNote) {
          return alert(`El producto ${missingNote.name} requiere una nota obligatoria.`);
        }

        if(this.withoutClient) {
          this.form.client = {
            name: "Cliente por defecto",
            email: null,
            phone: "0414409888391",
            address: "San diego",
            identification: "0000",
            balance: 0,
            typeClient: "a",
            companyName: null,
            id: 1
          }
          this.form.products = this.cart
          return this.form.post('/orders')
        }
        this.mode = 'client'
      } else if(this.mode === 'client') {
        this.form.products = this.cart
        this.form.post('/orders')
      } else if(this.mode === 'pay') {
        this.mode = 'print'
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.cart = this.cart.filter(p => p.id !== product.id)
      }
    },

    addCart(product) {
      const isRecarga = this.isPlanProduct(product.code) || this.isCustomBsProduct(product.code)
      if (!isRecarga && product.quantity <= 0) {
        return alert('No hay stock disponible de este producto')
      }

      const existingProduct = this.cart.find(p => p.id === product.id)
      
      if (existingProduct) {
        existingProduct.quantity++
      } else {
        this.cart.push(this.buildCartItem(product))
      }
    },

    onSearchProductsChange() {
      this.$inertia.get('/pos', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        }
      )
    },

    findClient() {
      this.$inertia.get('/pos', pickBy(this.form), { preserveState: true })

      setTimeout(() => {
        if (this.client) {
          this.textSearch = 'Buscar cliente'
          this.showClientCreate = true
          this.form.client = {
            name: this.client.name,
            email: this.client.email,
            phone: this.client.phone,
            address: this.client.address,
            identification: this.client.identification,
            balance: this.client.balance,
            typeClient: this.client.typeClient || 'a',
            companyName: this.client.companyName,
          }
        } else {
          alert('No se encontró el cliente. Puede registrarlo en el formulario.')
          this.showClientCreate = true
        }
      }, 1500)
    },

    clearAllClientFields() {
      this.form.client = {
        name: '',
        email: '',
        phone: '',
        address: '',
        identification: '',
        balance: 0,
        typeClient: 'a',
        companyName: null,
      }
      this.showClientCreate = false
      this.textSearch = 'Buscar cliente'

      if (typeof window !== 'undefined' && window.location.search && window.location.search.includes('client')) {
        this.$inertia.get('/pos', pickBy({ search: this.form.search }), {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        })
      }
    },

    resetBalance() {
      this.form.client.balance = 0
      if (typeof window !== 'undefined' && window.location.search && window.location.search.includes('client')) {
        this.$inertia.get('/pos', pickBy({ search: this.form.search }), {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        })
      }
    },
  },

  mounted() {
    document.getElementById("search").focus()
  }
}
</script>
